import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { AnalyticsService } from './services/analytics.service';
import { AppService } from './services/app.service';
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import { AccountService } from './services/account.service';
import { InvoiceService } from '../../../einvoice-viewer/src/public-api';
import type { EInvoiceFormatMatch, EInvoiceValidationResult } from '../../../einvoice-viewer/src/public-api';
import type { EInvoiceValidationMode, EInvoiceViewerConfiguration } from '../../../einvoice-viewer/src/public-api';
import { ValidationMessage, ValidationResultDialogComponent } from './shared/modals/validation-result-dialog/validation-result-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from './shared/modals/error-dialog/error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EInvoiceViewerService } from '../../../einvoice-viewer/src/lib/services/einvoice-viewer.service';
import { environment } from '../environments/environment';

interface LaunchParameters {
  files?: Array<{ getFile(): Promise<File> }>;
}

interface LaunchQueue {
  setConsumer(consumer: (parameters: LaunchParameters) => void | Promise<void>): void;
}

declare global {
  interface Window {
    launchQueue?: LaunchQueue;
  }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit {
  dragover: boolean = false;
  @ViewChild('fileInputHidden') fileInput: ElementRef | undefined;
  @ViewChild('dropzone') dropzone: ElementRef | undefined;
  private lastValidationMessages: ValidationMessage[] = [];
  private pendingValidation: { xml: string; matchedFormat: EInvoiceFormatMatch; filename: string } | null = null;
  readonly showRecommendations = toSignal(
    inject(Router).events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => !event.urlAfterRedirects.startsWith('/start'))
    ),
    { initialValue: false }
  );
  readonly analytics: AnalyticsService;

  constructor(
    private appService: AppService,
    private router: Router,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private accountService: AccountService,
    private metaService: Meta,
    private einvoiceViewerService: EInvoiceViewerService,
    private invoiceService: InvoiceService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private titleService: Title,
  ) {
    // icons must be registered before the analytics service may open the consent dialog
    console.log('version:' + environment.version);
    this.registerSvgIcons();
    this.analytics = inject(AnalyticsService);
    if (window.launchQueue) {
      window.launchQueue.setConsumer(async (launchParams) => {
        try {
          console.debug("PWA launched with", launchParams);

          if (!launchParams || !launchParams.files || launchParams.files.length === 0) {
            console.warn("No files provided in PWA launch parameters");
            return;
          }

          const fileHandle = launchParams.files[0];
          if (!fileHandle) {
            console.warn("No file handle available in PWA launch");
            return;
          }

          const file: File = await fileHandle.getFile();
          console.debug("PWA file loaded:", file.name, file.type, file.size);

          // Trigger file processing through the normal flow
          this.einvoiceViewerService.filesAdded$.next([file]);
        } catch (error) {
          console.error("Error handling PWA file launch:", error);
          // Show user-friendly error message
          setTimeout(() => {
            this.showError(
              $localize`:@@einvoice.error.pwa-launch:Failed to open file from PWA launcher: ${error}:error:`
            );
          }, 1000); // Delay to ensure dialog service is available
        }
      });
    }
  }
  dragOver() {
    this.dragover = true;
    this.einvoiceViewerService.dragOver();
  }
  dragLeave() {
    this.dragover = false;
    this.einvoiceViewerService.dragLeave();
  }
  addFile() {
    this.fileInput?.nativeElement.click();
  }
  onFileChangeClick($event: any) {
    const files = $event.target.files;
    this.onFileChange(Array.from(files));
  }
  async onFileChange(pFileList: File[] | any) {
    this.dragover = false;
    this.einvoiceViewerService.filesAdded$.next(pFileList);
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  ngOnInit(): void {
    const title = $localize`:@@einvoice.meta.title:E-Invoice Viewer: Read e-invoices for free`;
    const description = $localize`:@@einvoice.meta.description:Free online e-invoice viewer. Our viewer displays your e-invoice just like a PDF invoice! Whether it is XRechnung or ZUGFeRD!`;
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: "description", content: description });
    this.metaService.updateTag({ property: "og:title", content: title });
    this.metaService.updateTag({ property: "og:description", content: description });
    this.metaService.updateTag({ name: "twitter:title", content: title });
    this.metaService.updateTag({ name: "twitter:description", content: description });
    this.metaService.updateTag({ property: "og:locale", content: this.accountService.Locale === 'en' ? 'en_US' : 'de_DE' });
    this.appService.setTitle(title);
    this.appService.applicationReady.subscribe((ready) => {
      if (ready) {
        this.router.navigate(['/']).catch(() => ({}));
      }
    });
    this.einvoiceViewerService.uploadClicked$.subscribe(() => {
      this.addFile();
    });
    this.einvoiceViewerService.filesAdded$.subscribe((files:File[]) => {
      try {
        this.processFile(files);
      } catch (error) {
        console.error("Error processing files:", error);
        this.showError($localize`:@@einvoice.error.processing:Error processing file: ${error}:error:`);
        this.einvoiceViewerService.setProcessing(false);
      }
    });

    if (this.accountService.showStartPage()) {
      this.router.navigate(['/start']).catch(() => ({}));
    }
    this.einvoiceViewerService.onError$.subscribe((error: {message: string, title?: string}) => {
      this.showError(error.message, error.title);
    });
    this.einvoiceViewerService.validationDetailsRequested$.subscribe(() => {
      if (this.lastValidationMessages.length > 0) {
        this.showValidationResult(this.lastValidationMessages);
      }
    });
    this.einvoiceViewerService.validationRequested$.subscribe(() => {
      if (this.getValidationMode() !== 'manual' || this.pendingValidation == null) {
        return;
      }

      void this.runBackgroundValidation(
        this.pendingValidation.xml,
        this.pendingValidation.matchedFormat,
        this.pendingValidation.filename,
      );
    });

  }

  processFile(files: File[]) {
    if (!files || files.length === 0) {
      console.warn("No files provided to processFile");
      this.einvoiceViewerService.setProcessing(false);
      return;
    }

    const reader = new FileReader();

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      this.showError($localize`:@@einvoice.error.file-read:Failed to read file: ${files[0].name}:filename:`);
      this.einvoiceViewerService.setProcessing(false);
    };

    reader.onloadend = async () => {
      try {
        let xmlData = reader.result;
        let xmlFileName = files[0].name;
        let additionalAttachments: any[] = [];
        let pdfBytes: Uint8Array | undefined;
        if (reader.result instanceof ArrayBuffer) {
          // potential Factur-X PDF
          const facturXContent = await this.invoiceService.extractXml(reader.result);
          if (facturXContent) {
            xmlData = facturXContent.xml;
            xmlFileName = facturXContent.xmlFileName;
            additionalAttachments = facturXContent.additionalAttachments;
            pdfBytes = facturXContent.pdfBytes;
          } else {
            this.showError($localize`:@@einvoice.error.not-supported:The provided PDF does not contain a supported e-invoice format.`);
            this.einvoiceViewerService.setProcessing(false);
            return;
          }
        }

        if (typeof xmlData === 'string') {
          const xml = xmlData;
          try {
            // 1. fast path: detect format + import model, then show the document ASAP
            const detection = await this.invoiceService.detectFormat(xml);
            if (detection.errors.length > 0) {
              this.showError(detection.errors.map((e: any) => e.message).join('\n'));
              return;
            }
            if (detection.matchedFormat == null) {
              this.showError($localize`:@@einvoice.error.detect-format:Failed to detect e-invoice format.`);
              return;
            }
            const matchedFormat = detection.matchedFormat;
            const processResult = await this.invoiceService.importModel(xml, matchedFormat.id, matchedFormat.version, matchedFormat.syntax);
            if (!processResult) {
              return;
            }
            if (!processResult.success) {
              this.showError(processResult.errors.map((e: any) => e.message ?? e).join('\n'));
              return;
            }

            this.einvoiceViewerService.setDocument(processResult.model, matchedFormat, additionalAttachments, xml, xmlFileName, pdfBytes);

            const filename = files[0].name;
            const name = filename.substring(0, filename.lastIndexOf('.')) || filename;
            this.titleService.setTitle(name + ' - ' + $localize`:@@einvoice.browser-title:e-invoice viewer`);
            this.pendingValidation = { xml, matchedFormat, filename };

            // document is visible now; release the blocking spinner
            this.einvoiceViewerService.setProcessing(false);

            // 2. optionally run validation in the background and surface status/progress
            if (this.getValidationMode() === 'auto') {
              void this.runBackgroundValidation(xml, matchedFormat, filename);
            }
          } catch (error) {
            this.showError(error instanceof Error ? error.message : String(error));
          }
        }
      } catch (error) {
        console.error("Error in processFile onloadend:", error);
        this.showError($localize`:@@einvoice.error.processing:Error processing file: ${error}:error:`);
      } finally {
        this.einvoiceViewerService.setProcessing(false);
      }
    };

    this.einvoiceViewerService.setProcessing(true);
    this.einvoiceViewerService.setValidationState({ status: 'idle' });
    this.lastValidationMessages = [];
    this.pendingValidation = null;

    try {
      if (files[0].type === 'application/pdf' || files[0].name.toLowerCase().endsWith('.pdf')) {
        reader.readAsArrayBuffer(files[0]);
      } else {
        reader.readAsText(files[0]);
      }
    } catch (error) {
      console.error("Error starting file read:", error);
      this.showError($localize`:@@einvoice.error.file-read:Failed to read file: ${files[0].name}:filename:`);
      this.einvoiceViewerService.setProcessing(false);
    }
  }


  private async runBackgroundValidation(xml: string, matchedFormat: EInvoiceFormatMatch, filename: string): Promise<void> {
    if (this.getValidationMode() === 'disabled' || this.einvoiceViewerService.validationState$.value.status === 'validating') {
      return;
    }

    this.einvoiceViewerService.setValidationState({
      status: 'validating',
      message: $localize`:@@einvoice.validation.status.validating:Validating…`
    });

    try {
      const result: EInvoiceValidationResult = await this.invoiceService.validate(xml, matchedFormat);

      const messages: ValidationMessage[] = [];
      this.addMessages(result, messages);
      this.lastValidationMessages = messages;

      const errorCount = messages.filter(m => m.severity === 'error').length;
      const warningCount = messages.filter(m => m.severity === 'warning').length;

      if (result.valid && errorCount === 0) {
        this.einvoiceViewerService.setValidationState({ status: 'valid', errorCount: 0, warningCount });
        this.snackBar.open($localize`:@@einvoice.file-loaded:e-invoice ${filename}:filename: has been successfully validated and loaded.`, undefined, {
          duration: 2000,
          panelClass: ['snackbar-success']
        });
      } else {
        this.einvoiceViewerService.setValidationState({ status: 'invalid', errorCount, warningCount });
        this.snackBar.open($localize`:@@einvoice.file-loaded-with-errors:Validation of e-invoice ${filename}:filename: failed. E-invoice with errors is displayed.`, undefined, {
          duration: 2000,
          panelClass: ['snackbar-error']
        });
      }
    } catch (error) {
      console.error('Background validation failed:', error);
      this.einvoiceViewerService.setValidationState({
        status: 'error',
        message: $localize`:@@einvoice.validation.status.failed:Validation failed`
      });
    }
  }

  private getValidationMode(): EInvoiceValidationMode {
    return this.getViewerConfiguration()?.validation?.mode ?? 'auto';
  }

  private getViewerConfiguration(): EInvoiceViewerConfiguration | undefined {
    return this.router.routerState.root.firstChild?.snapshot.data['configuration'] as EInvoiceViewerConfiguration | undefined;
  }

  private getLineNumber(location: unknown): number | undefined {
    if (typeof location !== 'object' || location === null || !('lineNumber' in location)) {
      return undefined;
    }
    return typeof location.lineNumber === 'number' ? location.lineNumber : undefined;
  }

  private getXmlLocation(location: unknown): string {
    if (typeof location !== 'object' || location === null || !('location' in location)) {
      return '';
    }
    return typeof location.location === 'string' ? location.location : '';
  }

    private addMessages(result: EInvoiceValidationResult, messages: ValidationMessage[]) {
      for (const step of result.stepResults) {
        const stepName = step.name ?? '';
        if (step.type === 'xsd') {
          for (const error of step.errors) {
            messages.push({
              stepName,
              stepType: step.type,
              severity: 'error',
              message: error.message,
              code: null,
              location: this.getLineNumber(error.location) ? `Line ${this.getLineNumber(error.location)}` : ''
            });
          }
          for (const error of (step.warnings ?? [])) {
            messages.push({
              stepName,
              stepType: step.type,
              severity: 'warning',
              message: error.message,
              code: null,
              location: this.getLineNumber(error.location) ? `Line ${this.getLineNumber(error.location)}` : ''
            });
          }
        }
        if (step.type === 'schematron') {
          for (const error of step.errors) {
            messages.push({
              stepName,
              stepType: step.type,
              severity: 'error',
              message: error.message,
              code: error.id ?? null,
              location: this.getXmlLocation(error.location)
            });
          }
          for (const error of (step.warnings ?? [])) {
            messages.push({
              stepName,
              stepType: step.type,
              severity: 'warning',
              message: error.message,
              code: error.id ?? null,
              location: this.getXmlLocation(error.location)
            });
          }
        }
      }
    }
  showError(errorMsg: string, title?: string) {
    setTimeout(() => { //dirty hack to ensure dialog can be opened (Angular zone issue?)
      this.dialog.open(ErrorDialogComponent, {
        width: "40%",
        data: {
          title: title ?? $localize`:@@einvoice.error.title:Failed to open file...`,
          msg: errorMsg
        }
      });
    }, 400);
  }
  showValidationResult(validationResult: ValidationMessage[]) {
    this.dialog.open(ValidationResultDialogComponent, {
      width: "50%",
      data: validationResult
    });
  }
  registerSvgIcons() {
    this.matIconRegistry.addSvgIcon('cloud_upload', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/cloud_upload.svg'));
    this.matIconRegistry.addSvgIcon('pageview', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/pageview.svg'));
    this.matIconRegistry.addSvgIcon('zoom_in', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/zoom_in.svg'));
    this.matIconRegistry.addSvgIcon('zoom_out', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/zoom_out.svg'));
    this.matIconRegistry.addSvgIcon('print', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/print.svg'));
    this.matIconRegistry.addSvgIcon('info', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/info.svg'));
    this.matIconRegistry.addSvgIcon('error', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/error.svg'));
    this.matIconRegistry.addSvgIcon('warning', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/warning.svg'));
    this.matIconRegistry.addSvgIcon('check', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/check.svg'));
    this.matIconRegistry.addSvgIcon('close', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/close.svg'));
    this.matIconRegistry.addSvgIcon('expand_more', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/expand_more.svg'));
    this.matIconRegistry.addSvgIcon('verified_user', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/verified_user.svg'));
  }

  navigateMain(): void {
    this.router.navigate(['/']).catch(() => ({}));
  }
}

